<template>
  <div v-if="userdata">
    <b-card
      class="mb-1"
      no-body
    >
      <b-card-header>
        <div class="d-flex justify-content-start">
          <b-avatar
            :text="avatarText(userdata.fullName)"
            :variant="`light-${resolveAvatarVariant(userdata.fullName)}`"
            rounded
          />
          <div class="d-flex flex-column ml-1">
            <h4 class="mb-0">
              {{ userdata.fullName || (cid=== 0 ? $t('contributors.add-new') : null) }}
            </h4>
            <span class="card-text">
              <b-badge
                v-show="userdata.infos.status"
                pill
                :variant="`light-${resolveStatusVariant(userdata.infos.status)}`"
                class="text-capitalize"
              >
                {{ userdata.infos.status }}
              </b-badge>
            </span>
          </div>
        </div>
        <b-button-group>
          <b-button
            v-if="!isEditing"
            class="btn-icon"
            variant="outline-primary"
            @click="createDocX()"
          >
            <feather-icon
              class="text-nowrap d-block d-sm-none"
              icon="FileTextIcon"
            />
            <span class="text-nowrap d-none d-sm-block">{{ $t('attachments.create-docx') }}</span>
          </b-button>
          <b-button
            v-if="cid !== 0 && isUserAllowWrite"
            class="btn-icon"
            variant="outline-primary"
            @click="edit()"
          >
            <feather-icon
              class="text-nowrap d-block d-sm-none"
              :icon="isEditing ? 'XIcon' : 'EditIcon'"
            />
            <span class="text-nowrap d-none d-sm-block">{{ $t('common.' + (!isEditing ? 'edit' : 'close')) }}</span>
          </b-button>

          <b-button
            v-if="cid !== 0 && isUserAllowWrite && isUserAllowDelete"
            class="btn-icon"
            variant="outline-primary"
            @click="deletee()"
          >
            <feather-icon
              class="text-nowrap d-block d-sm-none"
              icon="TrashIcon"
            />
            <span class="text-nowrap d-none d-sm-block">{{ $t('common.delete') }}</span>
          </b-button>

          <b-button
            v-if="isUserAllowWrite"
            class="btn-icon"
            variant="outline-primary"
            :disabled="!somethingChanged || !userdata.fullName"
            @click="submit()"
          >
            <feather-icon
              class="text-nowrap d-block d-sm-none"
              icon="SaveIcon"
            />
            <span class="text-nowrap d-none d-sm-block">{{ $t('common.save') }}</span>
          </b-button>
        </b-button-group>
      </b-card-header>
    </b-card>

    <contributors-view-card
      v-if="!isEditing"
      id="contributors-view-card"
      v-model="userdata"
      :refaid="refaid"
      :tags-options="tagsOptions"
      :tags-colors="tagsColors"
    />

    <contributors-edit
      v-if="isEditing && isUserAllowWrite"
      id="contributors-edit"
      v-model="userdata"
      :refaid="refaid"
      :lookup-options="lookupOptions"
    />
    <donations-list
      v-if="cid !== 0"
      ref="donationsListTable"
      :cid="cid"
      @on-reloaded="onDonationsListReloaded"
      @on-submit="onDonationSubmit"
    />
    <projects-list
      v-if="cid !== 0"
      ref="projectsListTable"
      :is-children="true"
      :cid="cid"
      @on-project-hiden="onProjectHiden"
    />

    <docs-modal
      ref="docsModal"
      table="contributors"
      :refaid="refaid"
      :rename="userdata.fullName"
      :templates="templatesOptions"
    />

  </div>

</template>

<script>
import {
  BCard, BCardHeader, BButton, BButtonGroup, BBadge, BAvatar,
} from 'bootstrap-vue'
import axios from '@axios'
import { ref } from '@vue/composition-api'
// eslint-disable-next-line import/no-cycle
import router from '@/vue.router'
import DonationsList from '@/views/donations/DonationsList.vue'
import ProjectsList from '@/views/projects/ProjectsList.vue'
import ContributorsEdit from '@/views/contributors/ContributorsEdit.vue'
import ContributorsViewCard from '@/views/contributors/ContributorsViewCard.vue'
import authService from '@/vue.auth.service'
import lookupsService from '@/vue.lookups.service'
import DocsModal from '@/views/layouts/DocsModal.vue'
import {
  avatarText, resolveAvatarVariant, resolveStatusVariant, swalSaveOptions, swalCloseOptions, toastSuccessOptions, toastErrorOptions, clone, decode, swalDeleteOptions,
} from '../layouts/layouts.utils'

export default {
  components: {
    BCard,
    BCardHeader,
    BButton,
    BButtonGroup,
    BBadge,
    BAvatar,
    ContributorsViewCard,
    ContributorsEdit,
    DonationsList,
    ProjectsList,
    DocsModal,
  },
  data() {
    return {
      isUserAllowWrite: authService.isUserInRole('write'),
      isUserAllowDelete: authService.isUserInRole('delete'),
    }
  },
  async mounted() {
    const currentArea = await authService.currentArea()
    this.refaid = currentArea.aid
    this.areaSettings = currentArea.settings.defaults
    this.templatesOptions = currentArea.settings.templates
    if (this.templatesOptions) this.templatesOptions.sort((a, b) => (a.label > b.label ? 1 : -1))
    this.tagsOptions = await lookupsService.getMaps('contributors.tags', currentArea.aid, currentArea.settings.obsoletes)
    this.tagsColors = currentArea.settings.tags
    const options = {
      statusOptions: await lookupsService.getMaps('contributors.status', currentArea.aid),
      contributorTypeOptions: await lookupsService.getMaps('contributors.contributorType', currentArea.aid),
    }
    this.lookupOptions = options
    await this.refetchData()

    this.somethingChanged = false
    // eslint-disable-next-line no-underscore-dangle
    if (!this._events.changedArea) {
      this.$root.$on('changedArea', () => {
        this.$root.$off('changedArea')
        this.$router.push({ name: 'list-contributors' })
      })
    }
    this.$watch('userdata', () => {
      this.somethingChanged = true
    }, { deep: true })
  },

  beforeDestroy() {
    this.$root.$off('changedArea')
  },
  methods: {
    edit() {
      if (this.isEditing && this.somethingChanged) {
        this.$swal(swalSaveOptions).then(result => {
          if (result.value === true) {
            this.submit()
          } else if (result.value === false) {
            this.userdata = clone(this.original)
            setTimeout(this.reset, 500)
          }
        })
      } else this.isEditing = (!this.isEditing)
    },
    deletee() {
      this.$swal(swalDeleteOptions).then(async result => {
        if (result.value === true) {
          try {
            const resd = await axios.get('/api/record/contributors', { params: { aid: this.refaid, rid: this.cid } })
            resd.pleaseDelete = true
            await axios.post('/api/record/contributors',
              JSON.stringify(resd),
              { params: { aid: this.refaid, rid: this.cid } })
            this.$toast(toastSuccessOptions, { position: 'top-center' })
            this.$router.replace({ name: 'list-contributors' })
          } catch (error) {
            const message = toastErrorOptions
            if (error.response) message.props.text = decode(error.response.data.error)
            this.$toast(message, { position: 'top-center' })
          }
        }
      })
    },
    createDocX() {
      const { selected } = this.$refs.donationsListTable
      this.$refs.docsModal.show({ cid: this.cid, dids: selected }, async () => {
        const response = await axios.get('/api/record/contributors', { params: { aid: this.refaid, rid: this.cid } })
        this.userdata.lastUpdate = response.data.lastUpdate
        this.userdata.editor = response.data.editor
        this.userdata.attachments = response.data.attachments
        setTimeout(this.reset, 500)
      })
    },
    onDonationsListReloaded() {
      const newDonation = (router.currentRoute.query.newDonation && router.currentRoute.query.newDonation.toString().toLowerCase() === 'true')
      if (newDonation) {
        this.$refs.donationsListTable.addNew()
        this.$refs.donationsListTable.scrollToNew()
      }
    },
    onDonationSubmit() {
      this.$refs.projectsListTable.refresh()
    },
    onProjectHiden() {
      this.$refs.donationsListTable.refresh()
    },
    async submit() {
      const response = await axios.post('/api/record/contributors',
        JSON.stringify(this.userdata),
        { params: { aid: this.refaid, rid: this.cid } })

      lookupsService.refreshMaps(this.userdata.infos.status, 'contributors.status', this.refaid)
      lookupsService.refreshMaps(this.userdata.infos.contributorType, 'contributors.contributorType', this.refaid)
      lookupsService.refreshMaps(this.userdata.tags, 'contributors.tags', this.refaid)
      if (this.cid === 0) {
        this.$router.replace({ name: 'view-contributors', params: { id: response.data.rid } })
        await this.refetchData()
        this.reset()
      } else {
        this.userdata.editor = response.data.editor
        this.userdata.lastUpdate = response.data.lastUpdate
        this.original = clone(this.userdata)
        this.$toast(toastSuccessOptions, { position: 'top-center' })
        setTimeout(this.reset, 500)
      }
    },
  },
  setup() {
    const refaid = ref(null)
    const areaSettings = ref(null)
    const cid = ref(null)
    const userdata = ref(null)
    const original = ref(null)
    const somethingChanged = ref(false)
    const isEditing = ref(false)
    const tagsOptions = ref([])
    const tagsColors = ref([])
    const lookupOptions = ref([])
    const templatesOptions = ref([])

    const reset = () => {
      isEditing.value = false
      somethingChanged.value = false
    }
    const refetchData = async () => {
      cid.value = parseInt(router.currentRoute.params.id, 0)
      if (cid.value === 0) {
        userdata.value = {
          fullName: null,
          infos: {
            status: areaSettings.value.contributorStatus,
            notes: null,
            address: {
              state: areaSettings.value.state,
            },
          },
        }
        original.value = null
        isEditing.value = true
      } else if (cid.value) {
        const response = await axios.get('/api/record/contributors', { params: { aid: refaid.value, rid: cid.value } })
        if (!response.data.infos) response.data.infos = {}
        if (!response.data.infos.address) response.data.infos.address = {}
        userdata.value = response.data
        original.value = clone(userdata.value)
      }
    }

    return {
      refaid,
      areaSettings,
      cid,
      userdata,
      original,
      isEditing,
      somethingChanged,
      tagsOptions,
      tagsColors,
      lookupOptions,
      templatesOptions,
      reset,
      refetchData,
      avatarText,
      resolveAvatarVariant,
      resolveStatusVariant,
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (this.somethingChanged || (this.$refs.donationsListTable && this.$refs.donationsListTable.somethingChanged)) {
      const result = await this.$swal(swalCloseOptions)
      if (!result.value) {
        next(false)
        return
      }
    }
    next()
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>
